import { useEffect } from "react";
import { useRouter } from "next/router";
import dynamic from "next/dynamic";
import slugify from "slugify";

import { fetchLayoutDataServer } from "lib/api/strapi-layout";
import Layout from "components/Layout";
import Guide from "components/guide/Guide";

import {
  fetchTravelGuide,
  fetchTravelGuidePaths,
} from "lib/api/strapi-travel-guide";
import { site } from "config";
import { fetchRelatedExperiences } from "lib/api/strapi-experiences";
import { formatGuideCountrySlug } from "utils/guides/slug";
import { fetchFoodGuideByDestination } from "lib/api/strapi-food-guide";

// import styles from "./TravelGuide.module.scss";

export default function TravelGuide({
  guide,
  relatedExperiences,
  layoutData,
  foodGuide,
}) {
  const router = useRouter();

  const metaTitle = guide?.seo?.metaTitle
    ?.split("-")
    ?.map((word) => {
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");

  const canonicalUrlSlug = `travel-guides/${formatGuideCountrySlug({
    location: guide?.location,
  })}/${guide.slug}`;

  const seo = {
    ...guide?.seo,
    metaTitle,
    canonicalURL: `${site}/${canonicalUrlSlug}`,
  };

  useEffect(() => {
    if (
      !router.isReady ||
      window?.location?.href?.includes("localhost") ||
      window?.location?.href?.includes("vercel.app")
    ) {
      return;
    }

    const pathMatchesCanonical = router.asPath === canonicalUrlSlug;

    if (pathMatchesCanonical) {
      return;
    }

    if (!pathMatchesCanonical) {
      router.replace(`${site}/${canonicalUrlSlug}`, undefined, {
        shallow: true,
      });
    }
  }, [canonicalUrlSlug, router.isReady]);

  return (
    <Layout layoutData={layoutData} seo={seo}>
      <Guide
        data={guide}
        relatedExperiences={relatedExperiences}
        foodGuide={foodGuide}
      />
    </Layout>
  );
}

export async function getStaticProps({ params }) {
  const { slug } = params;
  const guideData = await fetchTravelGuide({ slug });

  if (!guideData) {
    return {
      notFound: true,
    };
  }

  const destinationSlug =
    guideData?.location?.destinations[0]?.slug.toLowerCase();

  const relatedExperiences = await fetchRelatedExperiences({
    country:
      guideData?.location?.destinations[0]?.destination_title.toLowerCase(),
    process: true,
  });

  const foodGuide = await fetchFoodGuideByDestination({ destinationSlug });

  const layoutData = await fetchLayoutDataServer();

  return {
    props: {
      guide: guideData,
      relatedExperiences,
      foodGuide,
      layoutData,
    },
    revalidate: 86400,
  };
}

export async function getStaticPaths() {
  const pathData = await fetchTravelGuidePaths();

  const filteredPaths = pathData?.filter((guide) => {
    const { slug, location } = guide?.attributes;
    const country = location?.country;

    return location && slug && country;
  });

  const paths = filteredPaths.map((guide) => {
    const { slug, location } = guide.attributes;

    let country = formatGuideCountrySlug({ location });

    return {
      params: {
        country: slugify(country, {
          lower: true,
        }),
        slug: slug,
      },
    };
  });

  return {
    paths,
    fallback: "blocking",
  };
}
